import React, { useEffect, useState } from "react";
import { Table, Button, Typography, Space, Tag, Dropdown, Input, Select } from "antd";
import { useNavigate } from "react-router-dom";
import MentorCourseApiService from '../services/MentorCourseApiService';
import { DownOutlined, EyeOutlined, ToolOutlined, SearchOutlined, BookOutlined, FilterFilled } from "@ant-design/icons";
import '../styles/MentorCourses.css';
import { debounce } from '../utils/debounce';
import ViewCourseDetailModal from '../components/courses/ViewCourseDetailModal';
import CoursesApiService from '../services/CoursesApiService';
const { Title } = Typography;

const MentorCourses = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [searchText, setSearchText] = useState("");
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const navigate = useNavigate();

  const fetchCourses = async (page = 1, pageSize = 10, search = "", status = statusFilter) => {
    setLoading(true);
    try {
    
      const res = await MentorCourseApiService.getMentorCourses({ page, pageSize, search, status });
      console.log('BE pagination:', res.pagination); 
      setCourses(res.courses || []);
      setPagination({
        current: res.pagination?.currentPage || 1,
        pageSize: res.pagination?.itemsPerPage || 10,
        total: res.pagination?.totalItems || 0
      });
    } catch (e) {
      setCourses([]);
      setPagination({ current: 1, pageSize: 10, total: 0 });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchCourses(1, pagination.pageSize, searchText, statusFilter);
    // eslint-disable-next-line
  }, [statusFilter]);

  // Debounce search
  const debouncedFetchCourses = React.useRef(debounce((value) => {
    fetchCourses(1, pagination.pageSize, value);
  }, 500)).current;

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
    debouncedFetchCourses(e.target.value);
  };

  const handleTableChange = (pag, filters) => {
    // Lấy filter status từ filters
    let newStatus = 'all';
    if (filters && filters.isActive && filters.isActive.length > 0) {
      newStatus = filters.isActive[0] ? 'active' : 'inactive';
    }
    setStatusFilter(newStatus);
    fetchCourses(pag.current, pag.pageSize, searchText, newStatus);
  };

  const handleViewCourseDetail = async (course) => {
    try {
      setLoading(true);
      const response = await CoursesApiService.getCourseById(course._id);
      setSelectedCourse(response.data.course);
      setIsViewModalOpen(true);
    } catch (error) {
      setSelectedCourse(null);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "No",
      dataIndex: "no",
      key: "no",
      align: "center",
      width: 60,
      render: (text, record, index) => index + 1,
    },
    {
      title: "URL-friendly",
      dataIndex: "slug",
      key: "slug",
      align: "center",
      width: 180,
      render: (text, record) => (
        <Button
          type="link"
          style={{
            color: '#1a4c91',
            fontWeight: 400,
            textDecoration: 'none',
            padding: 0,
            whiteSpace: 'normal',      // xuong dong tu nhien
            wordBreak: 'break-word',   // only xuong dong tai cac gach ngang va khoang trang
            maxWidth: 180,
            minWidth: 0,
            lineHeight: 1.3,
            display: 'inline-block'
          }}
          onMouseOver={e => e.currentTarget.style.textDecoration = 'underline'}
          onMouseOut={e => e.currentTarget.style.textDecoration = 'none'}
          onClick={() => handleViewCourseDetail(record)}
        >
          <span style={{
            overflowWrap: 'break-word',
            wordBreak: 'break-word',
            whiteSpace: 'normal'
          }}>
            {text}
          </span>
        </Button>
      ),
    },
    {
      title: "Course Name",
      dataIndex: "name",
      key: "name",
      align: "center",
      width: 340,
    },
    {
      title: "Author",
      dataIndex: "author",
      key: "author",
      align: "center",
      width: 160,
    },
    {
      title: "Category",
      dataIndex: ["category", "name"],
      key: "category",
      align: "center",
      width: 180,
      render: (_, record) => record.category?.name || "",
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      align: "center",
      width: 120,
      render: (price) => `$${price?.toFixed(2)}`,
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      align: "center",
      width: 120,
      filters: [
        { text: 'Active', value: true },
        { text: 'Inactive', value: false }
      ],
      filterMultiple: false,
      filteredValue: statusFilter === 'all' ? null : [statusFilter === 'active'],
      onFilter: (value, record) => record.isActive === value,
      render: (isActive) => (
        <Tag color={isActive ? "success" : "error"}>{isActive ? "Active" : "Inactive"}</Tag>
      ),
    },
    {
      title: "Action",
      key: "Action",
      align: "center",
      width: 180,
      render: (_, record) => (
        <Button
          type="default"
          style={{
            borderRadius: 20,
            fontWeight: 500,
            background: '#e6f0ff',
            color: '#1677ff',
            border: '1.5px solid #91caff',
            boxShadow: 'none',
            minWidth: 40,
          }}
          icon={<BookOutlined style={{ fontSize: 16, marginRight: 4, color: '#1677ff' }} />}
          onClick={() => navigate(`/learningzone/${record.slug}?role=mentor`)}
        >
          Learning Zone
        </Button>
      ),
    },
  ];

  return (
    <div className="mentor-courses-container">
      <Title level={3} style={{ marginBottom: 24, color: "#1a4c91" ,fontWeight:500, fontSize:32}}>
      Mentor's Course</Title>
      <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
        <Input.Search
          placeholder="Search by course name..."
          allowClear
          enterButton={
            <Button
              type="default"
              style={{ fontWeight: 500 }}
              icon={<SearchOutlined />}
            />
          }
          style={{ width: 320 }}
          value={searchText}
          onChange={handleSearchChange}
          onSearch={value => {
            setSearchText(value);
            fetchCourses(1, pagination.pageSize, value, statusFilter);
          }}
        />
      </div>
      <Table
        className="mentor-courses-table"
        columns={columns.map((col) => {
          // Add custom cell className for each column
          return {
            ...col,
            onCell: (record, rowIndex) => ({
              className: `mentor-courses-col-${col.key}`
            })
          };
        })}
        dataSource={courses}
        rowKey={r => r._id || r.slug}
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total, 
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `Total ${total} courses`,
        }}
        onChange={handleTableChange}
        bordered
      />
      <ViewCourseDetailModal
        visible={isViewModalOpen}
        onCancel={() => { setIsViewModalOpen(false); setSelectedCourse(null); }}
        course={selectedCourse}
      />
    </div>
  );
};

export default MentorCourses; 